import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Switch, Tab, Tabs, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { helpWhy } from '../../help/why';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { PayrollProvisionService } from '../../services/payrollProvision.service';
import { Account } from '../../interfaces/accounting.interface';
import { IProvisionSettings, ISuggestedAccountsResult } from '../../interfaces/payrollProvision.interface';
import { leafAccounts } from '../../utils/postingAccounts';
import GratuityProvisionTab from './GratuityProvisionTab';
import FestivalProvisionTab from './FestivalProvisionTab';

type AccountKey =
    | 'gratuityExpenseAccountId' | 'gratuityProvisionAccountId'
    | 'festivalBonusExpenseAccountId' | 'festivalBonusProvisionAccountId'
    | 'pfEmployerExpenseAccountId' | 'pfPayableAccountId';
type AccountForm = Record<AccountKey, string>;

const EMPTY_ACCOUNTS: AccountForm = {
    gratuityExpenseAccountId: '', gratuityProvisionAccountId: '',
    festivalBonusExpenseAccountId: '', festivalBonusProvisionAccountId: '',
    pfEmployerExpenseAccountId: '', pfPayableAccountId: '',
};

const FIELDS: { key: AccountKey; label: string; kind: 'EXPENSE' | 'LIABILITY'; hint: string }[] = [
    { key: 'gratuityExpenseAccountId', label: 'Gratuity expense', kind: 'EXPENSE', hint: 'Debited with the monthly gratuity provision.' },
    { key: 'gratuityProvisionAccountId', label: 'Gratuity provision (liability)', kind: 'LIABILITY', hint: 'Credited with the provision; cleared when a final settlement is paid.' },
    { key: 'festivalBonusExpenseAccountId', label: 'Festival bonus expense', kind: 'EXPENSE', hint: 'Debited with the monthly festival bonus provision.' },
    { key: 'festivalBonusProvisionAccountId', label: 'Festival bonus provision (liability)', kind: 'LIABILITY', hint: 'Credited with the provision; released when the bonus is paid through payroll.' },
    { key: 'pfEmployerExpenseAccountId', label: 'Provident fund employer expense', kind: 'EXPENSE', hint: 'Debited with the employer share of provident fund contributions.' },
    { key: 'pfPayableAccountId', label: 'Provident fund payable (liability)', kind: 'LIABILITY', hint: 'Credited with both shares; the fund balance owed to members.' },
];

const fromSettings = (s: IProvisionSettings): AccountForm => ({
    gratuityExpenseAccountId: s.gratuityExpenseAccountId ?? '',
    gratuityProvisionAccountId: s.gratuityProvisionAccountId ?? '',
    festivalBonusExpenseAccountId: s.festivalBonusExpenseAccountId ?? '',
    festivalBonusProvisionAccountId: s.festivalBonusProvisionAccountId ?? '',
    pfEmployerExpenseAccountId: s.pfEmployerExpenseAccountId ?? '',
    pfPayableAccountId: s.pfPayableAccountId ?? '',
});

// W3-J: statutory provisions (gratuity, festival bonus) and the accounts they and the provident fund post to.
const PayrollProvisions: React.FC = () => {
    const { showSuccess, showApiError } = useToast();
    const canManage = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const canSettings = usePermission(PERMISSIONS.PAYROLL_SETTINGS_MANAGE);
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    const [tab, setTab] = useState(0);
    const [accounts, setAccounts] = useState<AccountForm>(EMPTY_ACCOUNTS);
    const [enabled, setEnabled] = useState(false);
    const [expenseAccounts, setExpenseAccounts] = useState<Account[]>([]);
    const [liabilityAccounts, setLiabilityAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [suggesting, setSuggesting] = useState(false);
    const [suggested, setSuggested] = useState<ISuggestedAccountsResult | null>(null);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [settings, expenses, liabilities] = await Promise.all([
                PayrollProvisionService.getSettings(institutionId),
                AccountService.getAccountsByType('EXPENSE'),
                AccountService.getAccountsByType('LIABILITY'),
            ]);
            setAccounts(fromSettings(settings));
            setEnabled(!!settings.gratuityProvisionEnabled);
            setExpenseAccounts(Array.isArray(expenses) ? leafAccounts(expenses) : []);
            setLiabilityAccounts(Array.isArray(liabilities) ? leafAccounts(liabilities) : []);
        } catch (e) {
            showApiError(e, 'Failed to load the provision settings');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const req = {
                gratuityProvisionEnabled: enabled,
                gratuityExpenseAccountId: accounts.gratuityExpenseAccountId || null,
                gratuityProvisionAccountId: accounts.gratuityProvisionAccountId || null,
                festivalBonusExpenseAccountId: accounts.festivalBonusExpenseAccountId || null,
                festivalBonusProvisionAccountId: accounts.festivalBonusProvisionAccountId || null,
                pfEmployerExpenseAccountId: accounts.pfEmployerExpenseAccountId || null,
                pfPayableAccountId: accounts.pfPayableAccountId || null,
            };
            const saved = await PayrollProvisionService.updateSettings(institutionId, req);
            setAccounts(fromSettings(saved));
            setEnabled(!!saved.gratuityProvisionEnabled);
            showSuccess('Provision settings saved');
        } catch (e) {
            showApiError(e, 'Failed to save the provision settings');
        } finally {
            setSaving(false);
        }
    };

    const suggest = async () => {
        setSuggesting(true);
        try {
            const result = await PayrollProvisionService.createSuggestedAccounts(institutionId);
            setSuggested(result);
            showSuccess('Suggested accounts processed');
            await load();
        } catch (e) {
            showApiError(e, 'Failed to create the suggested accounts');
        } finally {
            setSuggesting(false);
        }
    };

    const gratuityOff = !loading && tab === 1 && !enabled;

    return (
        <Box data-help-page="payroll.provisions.page" sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h5">Statutory Provisions</Typography>
                {isSystemAdmin && (
                    <SearchableSelect
                        data-help="payroll.provisions.field.institution" label="Select Institution" options={institutions} value={institutionId}
                        onChange={id => setInstitutionId(id)} getOptionLabel={i => i.name} clearable={false}
                        sx={{ flex: '1 1 220px', maxWidth: 320 }}
                    />
                )}
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                <Tab label="Accounts" />
                <Tab label="Gratuity" />
                <Tab label="Festival bonus" />
            </Tabs>

            {gratuityOff && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Gratuity provisioning is switched off. Turn on "Enable gratuity provisioning" in the Accounts tab to post runs.
                </Alert>
            )}

            {tab === 0 && (
                loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            The suggested chart-of-accounts entries are a starting point. Verify them with your accountant before posting any run.
                        </Alert>
                        <FormControlLabel
                            data-help="payroll.provisions.field.enabled"
                            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} disabled={!canSettings} />}
                            label="Enable gratuity provisioning"
                        />
                        <Box data-help-section="payroll.provisions.section.accounts" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                            {FIELDS.map(f => (
                                <Box key={f.key} sx={{ flex: '1 1 320px', maxWidth: 480 }}>
                                    <AccountAutocomplete
                                        label={f.label}
                                        accounts={f.kind === 'EXPENSE' ? expenseAccounts : liabilityAccounts}
                                        value={accounts[f.key]}
                                        onChange={id => setAccounts(a => ({ ...a, [f.key]: id }))}
                                        placeholder="None"
                                        disabled={!canSettings}
                                    />
                                    <Typography variant="caption" color="text.secondary">{f.hint}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {canSettings && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                                <Button data-help="payroll.provisions.save" {...helpWhy(saving ? { kind: 'status', message: 'Saving is in progress.' } : !institutionId ? { kind: 'status', message: 'Choose an institution first.' } : null)} variant="contained" onClick={save} disabled={saving || !institutionId}>Save</Button>
                                <Button data-help="payroll.provisions.suggest" {...helpWhy(suggesting ? { kind: 'status', message: 'Creating accounts is in progress.' } : !institutionId ? { kind: 'status', message: 'Choose an institution first.' } : null)} variant="outlined" onClick={suggest} disabled={suggesting || !institutionId}>Create suggested accounts</Button>
                            </Box>
                        )}
                        {suggested && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                <div>Created: {suggested.created.length ? suggested.created.join(', ') : 'none'}</div>
                                <div>Already present: {suggested.present.length ? suggested.present.join(', ') : 'none'}</div>
                                <div>Skipped: {suggested.skipped.length ? suggested.skipped.join(', ') : 'none'}</div>
                            </Alert>
                        )}
                    </Paper>
                )
            )}
            {tab === 1 && <GratuityProvisionTab institutionId={institutionId} canManage={canManage} />}
            {tab === 2 && <FestivalProvisionTab institutionId={institutionId} canManage={canManage} />}
        </Box>
    );
};

export default PayrollProvisions;
